<!-- eslint-disable vue/no-v-html -->
<template>
  <div
    v-if="initiallySelectedCareType !== 'After school care'"
    class="projects-map"
    data-scroll-results
  >
    <h2 class="projects-map-title text-center">
      {{ title }}
    </h2>
    <p class="projects-map-intro text-center mb-8 lg:mb-14">
      {{ intro }}
    </p>

    <div class="projects-map-container relative flex flex-col overflow-hidden mb-7 lg:mb-0">
      <projects-legend
        :legend-data="legendData"
        :hide-legend="hideLegend"
        :locations="searchData"
        :i18n="i18n"
        @zoom-to-location="zoomToLocation"
        @reset-map="resetMap"
      />
      <div
        class="projects-map-container-right w-full h-[720px] relative z-10"
        :class="{ 'is-hidden': !mapIsActive }"
      >
        <l-map
          v-if="map.showMap"
          ref="map"
          :options="map.mapOptions"
          :center="map.center"
          :zoom="map.mapOptions.zoomBase"
          style="height: 100%"
          track-resize="true"
          @ready="onMapReady"
        >
          <l-tile-layer
            :url="map.url"
            :attribution="map.attribution"
          />
          <l-marker
            v-for="(marker) in projectItems"
            :key="marker.id"
            ref="markersRef"
            :lat-lng="{
              lng: marker.geoLocation.longitude,
              lat: marker.geoLocation.latitude,
            }"
            :icon="getMarkerIcon(marker.type)"
            :options="{
              alt: marker.name + (marker.type?.name ? ' (' + marker.type.name + ')' : ''),
              title: marker.name + (marker.type?.name ? ' (' + marker.type.name + ')' : '')
            }"
            @click="handleMarkerClick"
            @keydown.enter="handleMarkerClick"
            @keydown.space="handleMarkerClick"
            @focus="handleMarkerFocus(marker)"
            @popupopen="onPopupOpen"
            @popupclose="onPopupClose"
          >
            <l-popup
              :options="{closeButton: false, minWidth: 350, maxWidth: 500, autoClose: false, closeOnClick: false, closeOnEscapeKey: true}"
              @ready="setupPopupFocusTrap"
            >
              <a
                class="leaflet-popup-close-button"
                href="#close"
                role="button"
                :aria-label="i18n.projectDetailMap.Close"
                tabindex="0"
                @click.prevent="closeLeafletPopup()"
                @keydown.enter="closeLeafletPopup()"
                @keydown.space="closeLeafletPopup()"
              >
                ×
              </a>
              <div
                class="projects-map-marker-popup"
                role="dialog"
                aria-labelledby="popup-title"
                tabindex="0"
              >
                <div class="mb-2 flex gap-2 items-center">
                  <img
                    v-if="marker?.type?.popupIcon"
                    :src="marker.type.popupIcon"
                    class="w-4 h-4"
                    alt=""
                  >
                  <span
                    v-if="marker?.type?.name"
                    class="text-black"
                  >{{ marker.type.name }}</span>
                </div>
                <h3
                  id="popup-title"
                  class="text-primary-bravo text-xl mb-1"
                >
                  <a
                    v-if="marker?.link"
                    :href="marker.link"
                    class="projects-map-marker-popup-link"
                    tabindex="0"
                  >
                    {{ marker.name }}
                  </a>
                  <span v-else>{{ marker.name }}</span>
                </h3>
                <div class="flex mb-2">
                  <span
                    v-if="marker?.address?.addressLine"
                  >
                    {{ marker.address.addressLine }}
                  </span>
                  <span
                    v-if="marker?.address?.addressLine && marker?.address?.city"
                    class="ml-2 mr-2"
                  >-</span>
                  <span
                    v-if="marker?.address?.city"
                  >
                    {{ marker.address.city }}
                  </span>
                </div>
                <img
                  v-if="marker?.image"
                  :src="marker?.image"
                  :alt="`${i18n.projectDetailMap.ImageOf} ${marker.name}`"
                  class="max-w-full mb-2 rounded-sm"
                  tabindex="0"
                >
                <kf-rich-text>
                  <div
                    tabindex="0"
                    v-html="marker?.description"
                  />
                </kf-rich-text>
              </div>
            </l-popup>
          </l-marker>
          <l-control-zoom position="bottomright" />
        </l-map>
      </div>
    </div>
    <LanguageNotAvailableModal
      :is-open="languageNotAvailableModalIsOpen"
      :venue="languageNotAvailableModalVenueObject"
      @close="setLanguageNotAvailableModal"
    />
  </div>
</template>

<script>
import KfRichText from '@/components/base/rich-text/RichText.vue';
import LanguageNotAvailableModal from '@/components/language-selector/LanguageNotAvailableModal.vue';
import ProjectsLegend from '@/components/projects/ProjectsLegend.vue';
import { setDayCareString, trackEvent } from '@/lib/gtm/trackEvent';
import * as LeafLet from "leaflet";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "leaflet/dist/leaflet.css";
import { LControlZoom, LMap, LMarker, LPopup, LTileLayer } from "vue2-leaflet";
delete LeafLet.Icon.Default.prototype._getIconUrl;

LeafLet.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('@/assets/svg/map/map-marker.svg?inline'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const markerNbBestaand = new LeafLet.icon({
  iconUrl: require('@/assets/svg/map/map-marker-nb-bestaand.svg?inline'),
  iconSize: [56, 56],
  iconAnchor: [16, 37],
  popupAnchor: [11, -35],
});

const markerIkc = new LeafLet.icon({
  iconUrl: require('@/assets/svg/map/map-marker-ikc.svg?inline'),
  iconSize: [56, 56],
  iconAnchor: [16, 37],
  popupAnchor: [11, -35],
});

const markerNbNieuwbouw = new LeafLet.icon({
  iconUrl: require('@/assets/svg/map/map-marker-nb-nieuwbouw.svg?inline'),
  iconSize: [56, 56],
  iconAnchor: [16, 37],
  popupAnchor: [11, -35],
});

const normalIcon = new LeafLet.icon({
  iconUrl: require('@/assets/svg/map/map-marker.svg?inline'),
  iconSize: [52, 58],
  iconAnchor: [16, 37],
  popupAnchor: [11, -35],
});

const bigIcon = new LeafLet.icon({
  iconUrl: require('@/assets/svg/map/map-marker.svg?inline'),
  iconSize: [82, 88],
  iconAnchor: [31, 67],
  popupAnchor: [11, -55],
});

const baseLatLng = {
  lat: '52.0987495',
  lng: '5.1163504',
};

export default {
  name: 'KfProjectsMap',
  components: {
    ProjectsLegend,
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    LanguageNotAvailableModal,
    LControlZoom,
    KfRichText,
  },
  props: {
    i18n: {
      type: Object,
      default: () => {},
    },
    title: {
      type: String,
      default: '',
    },
    intro: {
      type: String,
      default: '',
    },
    locatorPostalCode: {
      type: String,
      default: '',
    },
    apiEndpoint: {
      type: String,
      default: null,
    },
    stadiaMapsApiKey: {
      type: String,
      default: process.env.VUE_APP_STADIA_MAPS_API_KEY,
    },
    latlng: {
      type: Object,
      default: () => ({}),
    },
    areaId: {
      type: Number,
      default: null,
    },
    careTypes: {
      type: Array,
      default: () => [],
    },
    initiallySelectedCareType: {
      type: String,
      default: null,
    },
    projectItems: {
      type: Array,
      default: () => [],
    },
    searchData: {
      type: Array,
      default: () => [],
    },
    legendData: {
      type: Array,
      default: () => [],
    }
  },
  data() {
    return {
      isLoadingMap: true,
      isLoadingResults: true,
      locationData: [],
      locationResults: [],
      locationResultsSplitted: {},
      locationMarkerData: [],
      locationMarkerRefs: [],
      locationClusterRefs: [],
      hideLegend: false,
      lastActiveMarker: null,
      isClosingPopup: false,
      currentOpenPopup: null,
      map: {
        showMap: true,
        center: LeafLet.latLng(baseLatLng.lat, baseLatLng.lng),
        url: `https://tiles.stadiamaps.com/tiles/osm_bright/{z}/{x}/{y}{r}.png?api_key=${this.stadiaMapsApiKey }`,
        attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
        icon: normalIcon,
        clusterOptions: {
          // See: https://github.com/Leaflet/Leaflet.markercluster#customising-the-clustered-markers
          iconCreateFunction: (cluster) => {
            return LeafLet.divIcon({
              html: '<span class="marker-cluster-inner">' + cluster.getChildCount() + '</span>',
              iconSize: new LeafLet.Point(46, 46),
              className: "marker-cluster",
            });
          }
        },
        mapOptions: {
          maxZoom: 16,
          minZoom: -16,
          zoomBase: 7.75,
          zoomDelta: 0.5,
          zoomSnap: 0,
          zoomControl: false,
          scrollWheelZoom: false,
        },
        showCurrentLocation: 0,
        currentBounds: null,
      },
      mapIsActive: true,
      languageNotAvailableModalIsOpen: false,
      languageNotAvailableModalVenueObject: {
        url: '',
        id: '',
      },
      trackEvent,
    };
  },
  computed: {
    trackData() {
      return {
        event: 'childcare_location',
        event_name: 'childcare_location',
        event_category: 'registration',
        event_action: 'chose_childcare_location',
        event_label: this.tabIsActive.childcareTypeName,
        location: this.locatorPostalCode,
        school: '',
        'count_searchresult': this.locationResults.length,
      }
    }
  },
  watch: {
    projectItems: {
      handler() {
        this.$nextTick(() => {
          setTimeout(() => {
            this.addFocusHandlersToMarkers();
          }, 500);
        });
      },
      deep: true
    }
  },
  async mounted() {
    if (this.apiEndpoint) {
      this.isLoadingMap = false;
      this.map.showCurrentLocation = 1;
    } else {
      // eslint-disable-next-line no-console
      console.warn(`No (valid) api endpoint provided: [${this.apiEndpoint}]`);
      this.isLoadingMap = false;
    }

    // Add a slight delay to ensure map is fully rendered
    setTimeout(() => {
      this.addFocusHandlersToMarkers();
    }, 1000);
  },
  methods: {
    onMapReady() {
      if (this.$refs.map && this.$refs.map.mapObject) {
        const mapObject = this.$refs.map.mapObject;

        const originalClosePopup = mapObject.closePopup;
        mapObject.closePopup = function() {
          this.currentOpenPopup = null;
          return originalClosePopup.apply(this, arguments);
        }.bind(this);

        // Add a click handler to close popups when clicking on the map
        mapObject.on('click', () => {
          mapObject.closePopup();
        });
      }
    },
    trackEvents(venue, location) {
      const gtmData = {... this.trackData,  school: venue.childcareResultLocationName, location: location.name}
      trackEvent(gtmData, this.$gtm.enabled());
      trackEvent({
        'event': 'registration',
        'event_name': 'chooseChildcareLocation',
        'event_category': 'childcareLocation',
        'event_action': 'chooseChildcareLocation',
        'childcare_type': setDayCareString(venue.childCareType[0]),
        'location_adres': location.address.addressLine,
        'location_place': location.address.city,
        'location_province': location.address.province,
        'mdm_id': venue.mdmId
      }, this.$gtm.enabled());
    },
    updateMarkerRefs() {
      if (this.$refs.markersRef) {
        this.locationMarkerRefs = this.$refs.markersRef.map((ref) => {
          return {
            lat: ref.latLng.lat,
            lng: ref.latLng.lng,
            icon: ref.icon,
            mapObject: ref.mapObject,
          };
        });

        this.$nextTick(() => {
          this.addFocusHandlersToMarkers();
        });
      }
   },
    getLocationsByDistance(distance) {
      return this.locationData.filter((location) => {
        return location.distance < distance;
      });
    },
    deselectLocations() {
      return new Promise((resolve) => {
        this.locationResults.forEach((location) => {
          location.isSelected = false;
        });
        resolve();
      });
    },
    getLocationFromResults(marker) {
      const { latitude, longitude } = marker.geoLocation;
      const currentLocation = this.locationResults.find((location) => {
        return location.geoLocation.latitude === latitude && location.geoLocation.longitude === longitude;
      });
      return currentLocation || null;
    },
    showMarkerIfCorrectCaretype(marker) {
      const locationCareTypes = [];
      marker?.venues?.forEach((venue) => {
        venue?.childCareType?.forEach((type) => {
          if (!locationCareTypes.includes(type)) {
            locationCareTypes.push(type);
          }
        })
      });
      return locationCareTypes.includes(this.tabIsActive.childcareTypeKey);
    },
    selectMarker(location) {
      const currentMarker = this.getMarkerRef(location);
      if (currentMarker) {
        currentMarker.mapObject.setIcon(bigIcon);
      }
    },
    deselectMarker(location) {
      const currentMarker = this.getMarkerRef(location);
      if (currentMarker) {
        currentMarker.mapObject.setIcon(normalIcon);
      }
    },
    getMarkerRef(location) {
      const { latitude, longitude } = location.geoLocation;
      const currentMarker = this.locationMarkerRefs.find((marker) => {
        return marker.lat === latitude && marker.lng === longitude;
      });
      return currentMarker || null;
    },
    toggleMobileMap() {
      this.mapIsActive = !this.mapIsActive;
    },
    isActiveCareType(venue) {
      return venue.childCareType.includes(this.tabIsActive.childcareTypeKey);
    },
    isInBoundingBox(latlng, sw, ne) {
      return ((latlng.latitude <= ne.lat && latlng.latitude >= sw.lat) && (latlng.longitude <= ne.lng && latlng.longitude >= sw.lng));
    },
    getDistance(startLat, startLon, destinationLat, destinationLng) {
      const R = 6371; // km
      const dLat = this.toRad(destinationLat-startLat);
      const dLon = this.toRad(destinationLng-startLon);
      const lat1 = this.toRad(startLat);
      const lat2 = this.toRad(destinationLat);

      const a = Math.sin(dLat/2) * Math.sin(dLat/2) + Math.sin(dLon/2) * Math.sin(dLon/2) * Math.cos(lat1) * Math.cos(lat2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
      return this.round(R * c, 1);
    },
    toRad(Value) {
      return Value * Math.PI / 180;
    },
    round(value, precision) {
      const multiplier = Math.pow(10, precision || 0);
      return Math.round(value * multiplier) / multiplier;
    },
    sortOnDistance(array, order = 'ASC') {
      return array.sort((a, b) => {
        if (a.distance < b.distance) {
          return order === 'ASC' ? -1 : 1;
        }
        if (a.distance > b.distance) {
          return order === 'ASC' ? 1 : -1;
        }
        return 0;
      });
    },
    splitLocationResults(locationResults) {
      const result = {};

      this.careTypes.forEach((careType) => {
        result[careType.childcareTypeKey] = [];

        locationResults.forEach((item) => {
          item.venues.forEach((venue) => {
            if (venue.childCareType.includes(careType.childcareTypeKey)) {
              result[careType.childcareTypeKey].push(item);
            }
          });
        });
      });

      return result;
    },
    getVenueBasedOnCareType(marker) {
      const childCareType = this.tabIsActive.childcareTypeKey;
      if (!childCareType) {
        return {};
      }
      const matchedVenue = marker.venues.filter((venue) => {
        return venue.childCareType.includes(childCareType);
      });
      return matchedVenue[0] || {};
    },
    closeLeafletPopup() {
      /**
       * The close-button is overwritten because Safari don't like the #close that Leaflet attached to the current URL
       * Safari looks for an id #close and jumps to it.
       *
       * This method don't attach something to the URL.
       */
      if (typeof this.$refs.map !== 'object') {
        return;
      }

      this.isClosingPopup = true;

      // Find and close all open popups
      if (this.$refs.map && this.$refs.map.mapObject) {
        const mapObject = this.$refs.map.mapObject;

        mapObject.eachLayer(layer => {
          if (layer._popup && layer._popup.isOpen()) {
            layer.closePopup();
          }
        });

        mapObject.closePopup();
      }

      this.hideLegend = false;
      this.currentOpenPopup = null;

      // Ensure we return focus to the marker
      this.$nextTick(() => {
        if (this.lastActiveMarker && document.body.contains(this.lastActiveMarker)) {
          this.lastActiveMarker.setAttribute('tabindex', '0');

          setTimeout(() => {
            try {
              if (this.lastActiveMarker && document.body.contains(this.lastActiveMarker)) {
                this.lastActiveMarker.focus();
              }
            } catch (e) {
              console.warn('Could not focus marker element:', e);
            }

            // Reset the flag after focus is restored
            setTimeout(() => {
              this.isClosingPopup = false;
            }, 100);
          }, 300);
        } else {
          this.isClosingPopup = false;
        }
      });
    },
    getLocalizedUrl(venue) {
      if (!Object.keys(venue)?.length) {
        return;
      }

      return venue.localizedUrls[document.documentElement.lang] || venue.url;
    },
    setLanguageNotAvailableModal(givenObject) {
      const object = givenObject || {};

      this.languageNotAvailableModalIsOpen = object.isOpen || false;
      this.languageNotAvailableModalVenueObject.url = object.url || '';
      this.languageNotAvailableModalVenueObject.id = object.id || '';
    },
    manageOtherLanguageModal(venue, location) {
      if (!venue || !Object.keys(venue).length) {
        return;
      }

      const currentLanguage = document.documentElement.lang;
      const url = this.getLocalizedUrl(venue);

      // Link click is prevented. Set window location to URL
      const  goToUrl = () => {
        if (location?.address?.addressLine) {
          this.trackEvents(venue, location)
        }
        window.location = url + `?zipcode=${this.locatorPostalCode}`
      }

      // NL? Go directly to URL.
      if (currentLanguage === 'nl') {
        goToUrl();

        return;
      }

      // If URL don't start with location? It's probably NL. Show modal
      if (!url.includes(`/${currentLanguage}/`)) {
        this.setLanguageNotAvailableModal({
          isOpen: true,
          url: url,
          id: venue?.id
        });

        return;
      }

      // Else.. Go to URL
      goToUrl();
    },
    getMarker(type) {
      if (type === 'NB bestaand') {
        return markerNbBestaand;
      } else if (type === 'IKC') {
        return markerIkc;
      } else if (type === 'NB Nieuwbouw') {
        return markerNbNieuwbouw;
      } else {
        return markerNbBestaand; // Or whatever default icon you want to use
      }
    },
    getMarkerIcon(marker) {
      if (marker && marker.markerIcon) {
        return new LeafLet.Icon({
          iconUrl: marker.markerIcon,
          iconSize: [56, 56],
          iconAnchor: [16, 37],
          popupAnchor: [11, -35],
        });
      } else {
        return marker.name === 'NB bestaand' ? markerNbBestaand :
               marker.name === 'IKC' ? markerIkc :
               marker.name === 'NB Nieuwbouw' ? markerNbNieuwbouw :
               normalIcon;
      }
    },
    onPopupOpen(event) {
      this.isPopupOpen = true;
      this.hideLegend = true;
      this.currentPopup = event.popup;

      // Store the marker that opened this popup so we can return focus to it later
      this.lastActiveMarker = document.activeElement;

      this.$nextTick(() => {
        const allMapMarkers = document.querySelectorAll('.leaflet-marker-icon');

        // Temporarily set all other markers to tabindex="-1" to prevent tabbing to them
        allMapMarkers.forEach(marker => {
          marker.setAttribute('data-original-tabindex', marker.getAttribute('tabindex') || '0');
          marker.setAttribute('tabindex', '-1');
        });

        // Make the popup close button the first tabbable element
        const closeButton = document.querySelector('.leaflet-popup-close-button');
        if (closeButton) {
          closeButton.setAttribute('tabindex', '0');
          closeButton.focus();
        }
      });
    },
    onPopupClose() {
      this.hideLegend = false;
      this.currentPopup = null;

      this.isClosingPopup = true;

      // Restore original tabindex for all map markers
      this.$nextTick(() => {
        try {
          const allMapMarkers = document.querySelectorAll('.leaflet-marker-icon');
          allMapMarkers.forEach(marker => {
            const originalTabindex = marker.getAttribute('data-original-tabindex');
            if (originalTabindex) {
              marker.setAttribute('tabindex', originalTabindex);
              marker.removeAttribute('data-original-tabindex');
            }
          });

          // Return focus to the marker that opened the popup
          if (this.lastActiveMarker && document.body.contains(this.lastActiveMarker)) {
            this.lastActiveMarker.setAttribute('tabindex', '0');

            setTimeout(() => {
              try {
                if (this.lastActiveMarker && document.body.contains(this.lastActiveMarker)) {
                  this.lastActiveMarker.focus();
                }
              } catch (e) {
                console.warn('Could not focus marker element:', e);
              }

              setTimeout(() => {
                this.isClosingPopup = false;
                this.lastActiveMarker = null;
              }, 100);
            }, 300);
          } else {
            this.isClosingPopup = false;
            this.lastActiveMarker = null;
          }
        } catch (e) {
          console.warn('Error in onPopupClose:', e);
          this.isClosingPopup = false;
          this.lastActiveMarker = null;
        }
      });
    },
    zoomToLocation(location) {
      console.log('Zooming to location:', location);
      const map = this.$refs.map.mapObject;
      map.setView([location.geoLocation.latitude, location.geoLocation.longitude], 12);
    },
    resetMap() {
      console.log('Reset map');
      const map = this.$refs.map.mapObject;
      map.setView([baseLatLng.lat, baseLatLng.lng], this.map.mapOptions.zoomBase);
    },
    setupPopupFocusTrap() {
      this.$nextTick(() => {
        try {
          const popup = document.querySelector('.leaflet-popup');
          if (!popup) return;

          const focusableElements = popup.querySelectorAll('a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])');
          if (!focusableElements.length) return;

          const firstFocusableElement = focusableElements[0];
          const lastFocusableElement = focusableElements[focusableElements.length - 1];

          // Add event listener to keep focus within the popup
          popup.addEventListener('keydown', (e) => {
            if (e.key === 'Tab') {
              if (e.shiftKey && document.activeElement === firstFocusableElement) {
                e.preventDefault();
                lastFocusableElement.focus();
              }
              else if (!e.shiftKey && document.activeElement === lastFocusableElement) {
                e.preventDefault();
                firstFocusableElement.focus();
              }
            }

            if (e.key === 'Escape') {
              this.closeLeafletPopup();
            }
          });

          // Focus the first element
          if (firstFocusableElement && document.body.contains(firstFocusableElement)) {
            firstFocusableElement.focus();
          }
        } catch (e) {
          console.warn('Error setting up popup focus trap:', e);
        }
      });
    },
    handleMarkerClick(event) {
      if (this.isClosingPopup) {
        if (event.originalEvent) {
          event.originalEvent.preventDefault();
          event.originalEvent.stopPropagation();
        }
        return false;
      }

      if (event.originalEvent) {
        event.originalEvent.preventDefault();
        event.originalEvent.stopPropagation();
      }

      // Get the marker that was clicked
      const marker = event.target;
      if (marker && marker.openPopup) {
        if (this.$refs.map && this.$refs.map.mapObject) {
          const mapObject = this.$refs.map.mapObject;

          // Close all existing popups
          Object.values(mapObject._layers).forEach(layer => {
            if (layer._popup && layer._popup.isOpen()) {
              layer.closePopup();
            }
          });

          mapObject.eachLayer(layer => {
            if (layer._popup && layer._popup.isOpen()) {
              layer.closePopup();
            }
          });

          mapObject.closePopup();
        }

        // Now open this marker's popup after a short delay
        setTimeout(() => {
          marker.openPopup();
          this.currentOpenPopup = marker;
        }, 50);
      }
    },
    handleMarkerFocus(marker) {
      if (this.isClosingPopup) {
        return;
      }

      const markerRef = this.$refs.markersRef.find(ref =>
        ref.latLng.lat === marker.geoLocation.latitude &&
        ref.latLng.lng === marker.geoLocation.longitude
      );

      if (markerRef && markerRef.mapObject) {
        setTimeout(() => {
          markerRef.mapObject.openPopup();
        }, 50);
      }
    },
    addFocusHandlersToMarkers() {
      // Wait for the next tick to ensure markers are fully rendered
      this.$nextTick(() => {
        try {
          // Process all markers
          if (this.$refs.markersRef) {
            this.$refs.markersRef.forEach((markerRef, index) => {
              if (markerRef && markerRef.mapObject) {
                const markerElement = markerRef.mapObject._icon;

                if (markerElement && document.body.contains(markerElement)) {
                  markerElement.removeEventListener('focus', this.onMarkerElementFocus);

                  // Add a focus event listener
                  markerElement.addEventListener('focus', () => {
                    if (!this.isClosingPopup &&
                        markerRef &&
                        markerRef.mapObject &&
                        index < this.projectItems.length) {
                      markerRef.mapObject.openPopup();
                    }
                  });
                }
              }
            });
          }
        } catch (e) {
          console.warn('Error adding focus handlers to markers:', e);
        }
      });
    },
  },
};
</script>

<style lang="postcss">
  .projects-map {

    &-container {
      &-right {
        height: 700px;

        &.is-hidden {
          @apply hidden;

          @screen lg {
            @apply block;
          }
        }
      }

      .leaflet-marker-icon {
        &.marker-cluster {
          @apply flex justify-center items-center bg-no-repeat;
          background-image: url('~@/assets/svg/map/map-cluster.svg?inline');
          background-size: 110%;
          background-position: 50% 0;

          .marker-cluster-inner {
            @apply text-lg font-bold;
          }
        }
      }
    }

    &-results {
      @apply flex flex-col h-full;

      &-nav {
        @apply flex;
      }

      &-btn {
        @apply p-5 text-sm w-1/2;

        &.is-active {
          @apply bg-gray-50;
        }
      }

      &-list {
        @apply hidden relative border-b border-gray-50;

        &-item {
          @apply flex p-8 pt-4;

          &.is-selected {
            a {
              @apply text-black;
            }

            .icon {
              @apply text-black transform translate-x-2;
            }
          }

          > h4 {
            @apply w-full;
          }
        }

        &.is-visible {
          @apply flex flex-col;
        }

        &-container {
          @apply h-full bg-gray-50 overflow-auto;

          &.is-hidden {
            @apply hidden;

            @screen lg {
              @apply block;
            }
          }
        }
      }

      &-link {
        @apply flex flex-col no-underline text-primary-bravo;

        &:hover {
          @apply text-black;

          .icon {
            @apply text-black transform translate-x-2;
          }
        }

        .icon {
          @apply text-primary-bravo fill-current mr-4 w-4;
          transition: color 300ms, transform 300ms;
        }
      }

      &-inner-title {
        @apply flex gap-4;

        &:not(:last-child) {
          @apply mb-6;
        }
      }

      &-distance {
        @apply absolute top-5 right-8 font-sans text-sm font-normal text-gray-500;
      }

      &-images {
        @apply flex flex-wrap justify-between;
      }

      &-image {
        @apply block object-cover object-center;
        height: 120px;
        width: 120px;

        @screen lg {
          @apply w-full;
          height: 120px;
        }

        &-container {
          @apply overflow-hidden;
          width: 48%;
        }
      }

    }

    &-toggle-btn {
      @apply block mx-auto;

      @screen lg {
        @apply hidden;
      }
    }

    .loader {
      z-index: 400;
    }
  }

  .leaflet-container {

    .leaflet-popup-content {
      margin: 0;
      width: 230px;
      overflow-x: hidden;
    }

    a.leaflet-popup-close-button {
      @apply top-2 right-2;

      &:focus {
        @apply outline-2 outline-secondary-delta;
        border-radius: 2px;
      }
    }
  }

  .projects-map-marker-popup {
    @apply font-sans p-3;

    &:focus {
      @apply outline-none;
      box-shadow: 0 0 0 2px var(--color-secondary-delta);
    }

    &-link {
      @apply no-underline;

      &:hover, &:focus {
        .marker-popup-title {
          @apply text-black;
        }
        @apply rounded-xs;
        outline: 2px solid var(--color-secondary-delta);
      }
    }

    [tabindex="0"]:focus {
      @apply rounded-xs;
      outline: 2px solid var(--color-secondary-delta);
    }
  }
</style>
